import { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, Plane } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let airportsDB = null;

async function loadLocalAirports() {
  if (airportsDB) return airportsDB;
  try {
    const res = await fetch("/airports.json");
    airportsDB = await res.json();
    return airportsDB;
  } catch {
    return [];
  }
}

function filterLocal(airports, query) {
  const q = query.toLowerCase();
  return airports
    .filter(
      (a) =>
        a.code.toLowerCase().includes(q) ||
        a.city.toLowerCase().includes(q) ||
        a.name.toLowerCase().includes(q) ||
        a.country.toLowerCase().includes(q)
    )
    .slice(0, 8);
}

export default function AirportAutocomplete({
  value,
  onChange,
  placeholder,
  testId,
  icon = "from",
}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Display text
  const displayText = value ? `${value.code} - ${value.city}` : "";

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);

    // Try SerpAPI first, then fall back to local
    try {
      const res = await axios.get(`${API}/flights/autocomplete`, {
        params: { query: q },
        timeout: 4000,
      });
      if (res.data && res.data.length > 0) {
        setSuggestions(res.data.slice(0, 8));
        setLoading(false);
        return;
      }
    } catch {
      // Fall through to local
    }

    // Local fallback
    const airports = await loadLocalAirports();
    setSuggestions(filterLocal(airports, q));
    setLoading(false);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setOpen(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 250);
  };

  const handleSelect = (airport) => {
    // Validate the code is a clean IATA code (2-4 alpha chars, no slashes)
    if (!airport.code || airport.code.includes("/") || airport.code.length > 4 || !/^[A-Za-z]+$/.test(airport.code)) {
      // Try to resolve from local airports DB by city name
      loadLocalAirports().then((airports) => {
        const match = airports.find(
          (a) => a.city.toLowerCase() === (airport.city || airport.name || "").toLowerCase()
        );
        if (match) {
          onChange({ ...match });
        } else {
          // Can't resolve — skip this invalid entry
          toast && toast.error("Please select a specific airport, not a city/region");
        }
      });
      return;
    }
    onChange(airport);
    setQuery("");
    setOpen(false);
  };

  const handleFocus = async () => {
    setOpen(true);
    if (!query && suggestions.length === 0) {
      // Show popular airports when empty
      const airports = await loadLocalAirports();
      setSuggestions(airports.slice(0, 6));
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        {icon === "from" ? (
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#f97316] pointer-events-none z-10" />
        ) : (
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#f97316] pointer-events-none z-10" />
        )}

        {value && !open ? (
          <button
            type="button"
            onClick={() => { onChange(null); setOpen(true); }}
            className="w-full border border-[#e2e8f0] rounded-xl pl-10 pr-4 py-3 bg-[#f8fafc] text-[#1f2937] text-sm text-left hover:border-[#1e3a5f]/30 transition-colors"
            data-testid={testId}
          >
            <span className="font-semibold">{value.code}</span>
            <span className="text-[#6b7280] ml-1.5">{value.city}, {value.country}</span>
          </button>
        ) : (
          <input
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={handleFocus}
            placeholder={placeholder}
            className="w-full border border-[#e2e8f0] rounded-xl pl-10 pr-4 py-3 bg-[#f8fafc] focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent outline-none transition-all text-[#1f2937] text-sm"
            data-testid={testId}
            autoComplete="off"
          />
        )}
      </div>

      {/* Dropdown */}
      {open && (suggestions.length > 0 || loading) && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#e2e8f0] rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {loading && (
            <div className="px-4 py-3 text-sm text-[#6b7280] flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
              Searching...
            </div>
          )}
          {suggestions.map((airport, i) => (
            <button
              key={`${airport.code}-${i}`}
              onClick={() => handleSelect(airport)}
              className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-[#f8fafc] transition-colors text-left border-b border-[#f1f5f9] last:border-0"
              data-testid={`airport-option-${airport.code}`}
            >
              <Plane className="w-4 h-4 text-[#6b7280] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-[#1e3a5f]">{airport.code}</span>
                  <span className="text-sm text-[#1f2937] truncate">{airport.city}</span>
                </div>
                <p className="text-xs text-[#6b7280] truncate">{airport.name}, {airport.country}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
